import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  Flex,
  Text,
  View,
  ProgressCircle,
  Divider
} from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers/rootReducer';
import {
  PermissionStatus,
  PermissionCheckResult,
  checkAllPermissions,
  getPermissionDisplayName
} from '../../utils/permission-utils';

interface PermissionDialogProps {
  isOpen: boolean;
  onPermissionsGranted: () => void;
  onClose?: () => void;
}

const PermissionDialog: React.FC<PermissionDialogProps> = ({
  isOpen,
  onPermissionsGranted,
  onClose
}) => {
  const [permissionResult, setPermissionResult] = useState<PermissionCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  // Get theme from Redux
  const { darkMode: globalDarkMode } = useSelector((state: RootState) => state.preferences);

  // Theme-aware colors
  const getThemeColors = () => {
    if (globalDarkMode) {
      return {
        primaryText: '#ffffff',
        secondaryText: '#b3b3b3',
        primaryBg: '#323232',
        secondaryBg: '#252525',
        successColor: '#22c55e',
        errorColor: '#ef4444',
        warningText: '#fbbf24'
      };
    } else {
      return {
        primaryText: '#4b4b4b',
        secondaryText: '#666666',
        primaryBg: '#ffffff',
        secondaryBg: '#f5f5f5',
        successColor: '#22c55e',
        errorColor: '#ef4444',
        warningText: '#cc6600'
      };
    }
  };

  const themeColors = getThemeColors();

  const checkPermissions = async () => {
    setIsChecking(true);

    try {
      const result = await checkAllPermissions();
      setPermissionResult(result);

      if (result.allPermissionsGranted) {
        setTimeout(() => {
          onPermissionsGranted();
        }, 1500);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
    } finally {
      setIsChecking(false);
    }
  };


  const renderPermissionItem = (permission: keyof PermissionStatus) => {
    if (!permissionResult) return null;

    const isGranted = permissionResult.permissions[permission];
    const displayName = getPermissionDisplayName(permission);

    // Third-party cookies are shown as informational only (no check/cross)
    if (permission === 'thirdPartyCookies') {
      return (
        <View key={permission} marginBottom="size-150">
          <Flex direction="row" alignItems="center" justifyContent="space-between">
            <Text UNSAFE_style={{ 
              fontSize: '14px', 
              fontWeight: '400', 
              color: themeColors.secondaryText 
            }}>
              {displayName}
            </Text>

            <Text UNSAFE_style={{ 
              fontSize: '12px', 
              fontWeight: '500',
              fontStyle: 'italic',
              color: themeColors.warningText
            }}>
              Required
            </Text>
          </Flex>
        </View>
      );
    }

    return (
      <View key={permission} marginBottom="size-150">
        <Flex direction="row" alignItems="center" justifyContent="space-between">
          <Text UNSAFE_style={{ 
            fontSize: '14px', 
            fontWeight: '400', 
            color: themeColors.secondaryText 
          }}>
            {displayName}
          </Text>

          <Text UNSAFE_style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: isGranted ? themeColors.successColor : themeColors.errorColor
          }}>
            {isGranted ? '✓' : '✗'}
          </Text>
        </Flex>
      </View>
    );
  };

  useEffect(() => {
    if (isOpen) {
      checkPermissions();
    }
  }, [isOpen]);


  if (!isOpen) return null;

  const allPermissionsGranted = permissionResult?.allPermissionsGranted ?? false;

  // Handler to open wiki in new tab
  const handleOpenWiki = () => {
    window.open('https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=CXTS&title=Yoddha+Access-Guidelines', '_blank', 'noopener,noreferrer');
  };

  return (
    <AlertDialog
      title="Browser Permissions Status"
      primaryActionLabel={allPermissionsGranted ? "OK" : "View Setup Guide"}
      variant={allPermissionsGranted ? "confirmation" : "information"}
      onPrimaryAction={allPermissionsGranted ? onClose : handleOpenWiki}
    >
      <View>
        {isChecking ? (
          <Flex direction="row" alignItems="center" justifyContent="center" gap="size-150" marginY="size-400">
            <ProgressCircle size="M" isIndeterminate />
            <Text UNSAFE_style={{ 
              fontSize: '16px', 
              color: themeColors.primaryText 
            }}>
              Checking permissions...
            </Text>
          </Flex>
        ) : permissionResult ? (
          <View>
            <Text marginBottom="size-400" UNSAFE_style={{ 
              color: themeColors.primaryText, 
              fontWeight: 'bold', 
              fontSize: '16px'
            }}>
              The application requires the following browser permissions:
            </Text>

            <View marginBottom="size-300" marginTop="size-200">
              {(Object.keys(permissionResult.permissions) as Array<keyof PermissionStatus>).map(renderPermissionItem)}
            </View>

            <Divider size="S" marginY="size-200" />

            {!allPermissionsGranted && (
              <View>
                <Text UNSAFE_style={{ 
                  color: themeColors.secondaryText, 
                  fontSize: '14px' 
                }}>
                  Please enable the required permissions in your browser settings. Click the button below for detailed setup instructions.
                </Text>
              </View>
            )}
          </View>
        ) : null}
      </View>
    </AlertDialog>
  );
};

export default PermissionDialog;